import React, { useMemo, useCallback } from 'react'

import { Building2 } from 'lucide-react'

import { BankDetails } from '@/core/api/entities/account/billingInfo'
import { useTranslation } from '@/core/i18n/useTranslation'

import { BankField } from './BankField'
import { BANK_FIELDS } from './PaymentMethods.constants'
import { BankFieldConfig } from './PaymentMethods.types'
import { hasValue } from './PaymentMethods.utils'

interface BankDetailsViewProps {
	bankDetails?: BankDetails
	companyTaxId?: string
}

export const BankDetailsView: React.FC<BankDetailsViewProps> = ({
	bankDetails,
	companyTaxId,
}) => {
	const { t } = useTranslation('common')

	const getFieldLabel = (key: string): string => {
		const labelMap: Record<string, string> = {
			bankName: t('payment_method.bank_fields.bank'),
			type: t('payment_method.bank_fields.type'),
			currency: t('payment_method.bank_fields.currency'),
			fullAccountNumber: t('payment_method.bank_fields.iban_cbu'),
			swift: t('payment_method.bank_fields.swift'),
			payTo: t('payment_method.bank_fields.pay_to'),
			address: t('payment_method.bank_fields.address'),
			taxId: t('payment_method.bank_fields.company_tax_id'),
			accountName: t('payment_method.bank_fields.account_name'),
			accountNumber: t('payment_method.bank_fields.account_number'),
			alias: t('payment_method.bank_fields.alias'),
			routing: t('payment_method.bank_fields.routing'),
			clabe: t('payment_method.bank_fields.clabe'),
			finalCreditAccount: t('payment_method.bank_fields.final_credit_account'),
		}
		return labelMap[key] || key
	}

	const renderBankField = useCallback(
		(field: BankFieldConfig) => {
			let value: string | undefined

			// Special handling for taxId
			if (field.key === 'taxId') {
				value = (bankDetails as any)?.[field.key] || companyTaxId
			} else {
				value = bankDetails?.[field.key as keyof BankDetails] as
					| string
					| undefined
			}

			if (!hasValue(value)) return null

			return (
				<BankField
					key={String(field.key)}
					label={getFieldLabel(String(field.key))}
					value={String(value)}
					fieldId={String(field.key)}
				/>
			)
		},
		[bankDetails, companyTaxId, t]
	)

	const leftColumnFields = useMemo(
		() => BANK_FIELDS.filter((field) => field.column === 'left'),
		[]
	)
	const rightColumnFields = useMemo(
		() => BANK_FIELDS.filter((field) => field.column === 'right'),
		[]
	)

	// Check if there are any bank details to show
	const hasBankDetails = useMemo(() => {
		if (!bankDetails) return false
		return BANK_FIELDS.some((field) => {
			if (field.key === 'taxId') {
				return hasValue((bankDetails as any)?.[field.key] || companyTaxId)
			}
			return hasValue(bankDetails[field.key as keyof BankDetails] as string)
		})
	}, [bankDetails, companyTaxId])

	// Show empty state if no bank details
	if (!hasBankDetails) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-6 text-center">
				<div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
					<Building2 className="w-8 h-8 text-muted-foreground" />
				</div>
				<h3 className="text-lg font-medium mb-2">
					{t('payment_method.bank_transfer_title', 'Bank Transfer')}
				</h3>
				<p className="text-muted-foreground max-w-md mb-4">
					{t(
						'payment_method.bank_transfer_not_available',
						'Bank transfer is not available for your account. Please use automatic payment (credit card or SEPA) to set up your payment method.'
					)}
				</p>
				<p className="text-sm text-muted-foreground">
					{t(
						'payment_method.contact_support_for_bank',
						'If you need to pay via bank transfer, please contact support.'
					)}
				</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<p className="text-sm text-muted-foreground mb-4">
				{t(
					'payment_method.bank_transfer_instructions',
					'Use the following bank details to make a manual transfer. Please include your account ID or invoice number as the payment reference.'
				)}
			</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div className="space-y-6">{leftColumnFields.map(renderBankField)}</div>
				<div className="space-y-6">
					{rightColumnFields.map(renderBankField)}
				</div>
			</div>
		</div>
	)
}
